import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { FaCheck, FaTimes } from "react-icons/fa";
import { FiInfo, FiTrash2, FiDownload, FiPlus } from "react-icons/fi";
import {
  getBills,
  updateBillStatus,
  deleteBill,
  getBillsSite,
} from "../../Services/billsService";
import {
  GenericTable,
  Spinner,
  Pagination,
  FilterCard,
  DownloadExcelBill,
  Tooltip,
  RoleValidator,
} from "../../Components/Common";
import BillCardView from "./Components/BillCardView";
import * as routes from "../../routes";
import {
  BILL_COLUMNS,
  BILL_LABELS,
  TOAST_MESSAGES,
  MISC,
  EXCEL_FILE_COLUMNS,
  DEFAULT_STATUS_OPTIONS,
  TYPES_OPTIONS,
} from "./constants";
import { UserContext } from "../../Context";
import { ROLES } from "../../Constants/Strings";
import { getUser, getAllUsers } from "../../Services/userService";
import { toProperCase } from "../../Utils";

const ViewBills = () => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loggedInUser, setLoggedInUser] = useState(null);

  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [filters, setFilters] = useState({ status: "", user: "", date: "" });
  const [tempFilters, setTempFilters] = useState(filters);
  const [users, setUsers] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const location = useLocation();

  const onPageChange = (newPage) => setPage(newPage);

  const onPageSizeChange = (newSize) => {
    setRowsPerPage(newSize);
    setPage(1); // reset to first page when page size changes
  };

  const fetchBills = async (pageNumber, currentFilters = filters) => {
    setLoading(true);
    try {
      const cleanedFilters = Object.fromEntries(
        Object.entries(currentFilters).filter(([_, val]) => val !== "")
      );

      let params = {
        limit: rowsPerPage,
        offset: (pageNumber - 1) * rowsPerPage,
        ...cleanedFilters,
      };

      if (loggedInUser) {
        if (loggedInUser.role === ROLES.SITE_ENGINEER) {
          // Only bills created by him
          params.user = loggedInUser.id;
        } else if (
          loggedInUser.role === ROLES.PROJECT_ENGINEER ||
          loggedInUser.role === ROLES.PROJECT_MANAGER
        ) {
          // Fetch bills only for sites assigned to them
          const siteRes = await getBillsSite(loggedInUser.id);
          const userSiteIds = siteRes?.data?.results?.map((s) => s.id) || [];
          params.site__in = userSiteIds; // pass as array of site ids
        }
        // Other roles: fetch all bills
      }

      const res = await getBills(params);
      const results = res?.data?.results || [];
      setBills(results);

      const count = res?.data?.count || 0;
      setTotalPages(Math.ceil(count / rowsPerPage));
    } catch (err) {
      toast.error(TOAST_MESSAGES.BILLS_FETCH_ERROR);
      setBills([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (location.state?.projectId) {
      const projectId = location.state.projectId;
      const projectName = location.state.projectName;

      // Add project filter
      const updatedFilters = { ...filters, project: projectId };
      setFilters(updatedFilters);
      setTempFilters(updatedFilters);

      toast.success(`Showing bills for project: ${projectName}`);
      fetchBills(1, updatedFilters);
    }
  }, [location.state]);

  useEffect(() => {
    if (loggedInUser) {
      fetchBills(page);
    }
  }, [page, rowsPerPage, loggedInUser, filters.project]);

  useEffect(() => {
    const fetchLoggedInUser = async () => {
      try {
        const res = await getUser();
        setLoggedInUser(res.data);
      } catch (err) {
        toast.error(err);
      }
    };
    fetchLoggedInUser();
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = (await getAllUsers({ limit: 10, offset: 0 })).data.results;
        setUsers(res);

        // Format for CommonFilterCard
        const formattedOptions = res.map((user) => ({
          value: user.id,
          label: `${user.first_name} ${user.last_name}`,
        }));
        setUserOptions(formattedOptions);
      } catch (err) {
        toast.error(TOAST_MESSAGES.USER_FETCH_ERROR);
      }
    };
    fetchUsers();
  }, []);

  const downloadExcelData = () => {
    DownloadExcelBill({
      fetchFn: getBills,
      fieldMappings: EXCEL_FILE_COLUMNS, // define mapping as in reference
      fileName: "Bills_Data.xlsx",
      filters: tempFilters,
    });
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateBillStatus(id, { action: status });
      toast.success(TOAST_MESSAGES.STATUS_UPDATE_SUCCESS(status));
      fetchBills(page);
    } catch {
      toast.error(TOAST_MESSAGES.STATUS_UPDATE_ERROR);
    }
  };

  const handleDeleteBill = async (id) => {
    const confirmed = window.confirm(TOAST_MESSAGES.DELETE_ALERT);
    if (!confirmed) return;

    setDeleting(true);
    try {
      await deleteBill(id);
      toast.success(TOAST_MESSAGES.BILL_DELETE_SUCCESS);
      fetchBills(page);
    } catch {
      toast.error(TOAST_MESSAGES.BILL_DELETE_ERROR);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-4">
      {/* Header && Bill */}
      <div className="flex flex-col">
        <div className="flex justify-between items-center mb-4 mt-4 md:mt-0">
          <h2 className="text-xl font-semibold">Reimbursement Bills</h2>
          <div className="flex gap-3">
            <Tooltip text="Download Excel">
              <button
                onClick={downloadExcelData}
                className="px-4 py-2 bg-primary text-white rounded-lg shadow hover:bg-secondary transition-colors"
              >
                <FiDownload />
              </button>
            </Tooltip>

            <Tooltip text="Add Bill">
              <RoleValidator allowedRoles={[ROLES.SITE_ENGINEER, ROLES.PROJECT_ENGINEER, ROLES.PROJECT_MANAGER]}>
                <button
                  onClick={() =>
                    navigate(routes.ADD_BILL.replace(":billType", "general"))
                  }
                  className="px-4 py-2 bg-primary text-white rounded-lg shadow hover:bg-secondary transition-colors"
                >
                  <FiPlus />
                </button>
              </RoleValidator>
            </Tooltip>
          </div>
        </div>
        <div className="mb-4">
          <FilterCard
            tempFilters={tempFilters}
            setTempFilters={setTempFilters}
            onFilterChange={(updated) => {
              setFilters(updated);
              fetchBills(1, updated);
            }}
            fields={[
              {
                key: "status",
                label: "Status",
                type: "select",
                options: DEFAULT_STATUS_OPTIONS,
              },
              ...(user?.role !== ROLES.SITE_ENGINEER &&
              user?.role !== ROLES.PROJECT_ENGINEER
                ? [
                    {
                      key: "user",
                      label: "User",
                      type: "select",
                      options: [{ label: "All", value: "" }, ...userOptions],
                    },
                  ]
                : []),
              {
                key: "category__type",
                label: "Type",
                type: "select",
                options: TYPES_OPTIONS,
              },
              { key: "date", label: "Date", type: "date" },
            ]}
          />
        </div>
      </div>

      {loading && bills.length === 0 ? (
        <div className="flex justify-center pt-10">
          <Spinner size={4} />
        </div>
      ) : bills.length === 0 ? (
        <div className="text-center text-gray-500 mt-10">
          {MISC.NO_BILLS_FOUND}
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block">
            <div className="overflow-y-auto h-[55vh] border rounded-md relative">
              <GenericTable
                tableColumns={BILL_COLUMNS.ADMIN}
                tableRows={bills}
                columnLabels={BILL_LABELS}
                renderField={(key, row) => {
                  const isPending = row.status
                    ?.toLowerCase()
                    .includes("pending");
                  if (key === "status") {
                    return toProperCase(row.status);
                  }

                  if (key === "actions") {
                    const isL1Pending = row.status === "PENDING_L1_APPROVAL";
                    const isL2Pending = row.status === "PENDING_L2_APPROVAL";
                    const isCreator = row.user === loggedInUser?.id;

                    return (
                      <div className="grid grid-cols-4 gap-3 text-lg text-center">
                        {/* View Button */}
                        <Tooltip text="View Bill">
                          <FiInfo
                            className="cursor-pointer text-primary hover:text-secondary"
                            onClick={() =>
                              navigate(
                                routes.VIEW_BILL.replace(":billId", row.id)
                              )
                            }
                          />
                        </Tooltip>

                        {/* L1 Approve/Reject - Project Engineer */}
                        {user?.role === ROLES.PROJECT_ENGINEER &&
                          isL1Pending && (
                            <>
                              <Tooltip text="Approve">
                                <FaCheck
                                  className="cursor-pointer text-green-600"
                                  onClick={() =>
                                    handleStatusChange(row.id, "approve")
                                  }
                                />
                              </Tooltip>
                              <Tooltip text="Reject">
                                <FaTimes
                                  className="cursor-pointer text-red-600"
                                  onClick={() =>
                                    handleStatusChange(row.id, "reject")
                                  }
                                />
                              </Tooltip>
                            </>
                          )}

                        {/* L2 Approve/Reject - Project Manager */}
                        {user?.role === ROLES.PROJECT_MANAGER &&
                          isL2Pending && (
                            <>
                              <FaCheck
                                className="cursor-pointer text-green-600"
                                onClick={() =>
                                  handleStatusChange(row.id, "approve")
                                }
                              />
                              <FaTimes
                                className="cursor-pointer text-red-600"
                                onClick={() =>
                                  handleStatusChange(row.id, "reject")
                                }
                              />
                            </>
                          )}

                        {isCreator && isPending ? (
                          <Tooltip text="Delete Bill">
                            <FiTrash2
                              className="cursor-pointer text-red-500 hover:text-red-700"
                              onClick={() => handleDeleteBill(row.id)}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip text="Delete Bill">
                            <FiTrash2 className="text-gray-500 hover:text-gray-600 cursor-not-allowed" />
                          </Tooltip>
                        )}
                      </div>
                    );
                  }

                  return row[key] || "-";
                }}
                sortingColumn="date"
              />
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="h-[64vh] overflow-y-auto block md:hidden">
            <BillCardView bills={bills} />
          </div>

          {deleting && (
            <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
              <Spinner />
            </div>
          )}

          <Pagination
            page={page}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage} // pass this
            onPageChange={onPageChange} // wrap setPage logic
            onPageSizeChange={onPageSizeChange} // pass this
          />
        </>
      )}
    </div>
  );
};

export default ViewBills;
